import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import DropDown from './DropDown';
import { Button } from './Core';

// Hooks
import useOutsideClick from '../../hooks/useOutsideClick';
import useScroll from '../../hooks/useScroll';
import useScreenSize from '../../hooks/useScreenSize';
import { useNavigate } from 'react-router-dom';

// Icons
import { Icon_Dots } from '../../styles/icons';

/**
 * Responsive Switcher (RSwitcher)
 *
 * Similar API to Switcher, but renders a responsive toggle:
 * - Desktop: pill button with currentOption text (secondary variant, like RButton)
 * - Mobile: round icon-only button (secondary variant) using Icon_Dots
 *
 * Props:
 * - type: string used in data-testid construction (default 'org')
 * - currentOption: label shown on desktop button (and aria-label on mobile)
 * - options: array of menu option objects { type: 'item'|'inactive'|'action'|'divider', id, name, path? }
 * - handleSelect: function(option) called when clicking an 'item'
 * - startPos: for keyboard nav in menu (default -1)
 * - bg: kept for parity with Switcher (unused)
 * - size: 'medium' | 'small'. Medium is the size the toggle has always been,
 *   sized to sit beside a form's own buttons; small is a step down for a menu
 *   that is a footnote to what it sits under rather than a peer of it. Applies
 *   to both forms. Omitted leaves every existing call site exactly as it was.
 * - round: render the icon-only button at every width, not only on mobile.
 *   A document line already carries its own label; a pill repeating it once per
 *   line would be noise, and the round form is what the mobile branch has
 *   always drawn.
 */
/*
 * One scale, two shapes. The values under `medium` are what the pill and the
 * circle already were, so naming the sizes changed nothing that did not ask for
 * a name.
 */
const SIZES = {
  medium: { diameter: '2.5rem', padInline: '0.75rem', padBlock: '0.375rem', font: 'var(--font-S)' },
  small:  { diameter: '2rem',   padInline: '0.6rem',  padBlock: '0.2rem',   font: 'var(--font-XS)' },
};

const CircleButton = styled(Button)`
  width: ${({ $diameter }) => $diameter || '2rem'};
  height: ${({ $diameter }) => $diameter || '2rem'};
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    pointer-events: none;
  }
`;

const PillButton = styled(Button)`
  ${({ $scale }) => $scale && `
    padding-inline: ${$scale.padInline};
    padding-block: ${$scale.padBlock};
    font-size: ${$scale.font};
  `}
`;

const MobileWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  margin-left: 0.5rem;
`;

const MobileLabel = styled.div`
  text-align: center;
  font-size: var(--font-XS);
  margin-top: 0.25rem;
  margin-right: 0.5rem;
  color: ${({ theme }) => theme.font.color};
`;

// `disabled`: the toggle does not open — an action is running (cardBusy).
const RSwitcher = ({ type = 'org', currentOption, options = [], handleSelect, startPos = null, bg = 'var(--clr-gray)', round = false, size, disabled = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const label = typeof currentOption === 'string' ? currentOption : '';
  const scale = SIZES[size] || null;

  if (startPos === null) {
    startPos = -1;
  }

  const { handleKeyNav } = useScroll(menuRef, isVisible, setIsVisible, options, startPos);

  const handleToggleClick = () => {
    setIsVisible(!isVisible);
  };

  const clickItem = (option) => {
    setIsVisible(false);
    handleSelect(option);
  };

  // Close menu when clicking outside
  useOutsideClick(menuRef, () => setIsVisible(false), 'dropdown');

  const manyItems = (options && options.length > 1);
  const items = (options || []).map((option, key) => {
    let testId;
    if (type === 'org') {
      testId = key - 1;
    } else {
      testId = option.id;
    }
    if (option.id === 'logOutButton') {
      testId = 'logout';
    }

    return (
      <React.Fragment key={`F-${testId}`}>
        {option.type === 'item' ? (
          <DropDown.Item
            data-testid={`${type}.switcher.${testId}`}
            key={option.id}
            onClick={() => clickItem(option)}
          >
            {manyItems ? option.name : option.name}
          </DropDown.Item>
        ) : null}
        {option.type === 'inactive' ? (
          <DropDown.Item $inactive key={option.id}>
            {option.name}
          </DropDown.Item>
        ) : null}
        {option.type === 'action' ? (
          <DropDown.Item
            data-testid={`${type}.switcher.${testId}`}
            className='action'
            key={option.id}
            onClick={() => {
              setIsVisible(false);
              navigate(option.path);
            }}
          >
            {option.name}
          </DropDown.Item>
        ) : null}
        {option.type === 'divider' ? <DropDown.Divider key={option.id} /> : null}
      </React.Fragment>
    );
  });

  return (
    <>
      <DropDown $margin='0 0s 0 0' name='Dropdown'>
        {(round || isMobile) ? (
          <MobileWrapper>
            <CircleButton
              type='button'
              name='Toggle'
              $diameter={scale?.diameter}
              $variant='secondary'
              onClick={handleToggleClick}
              onKeyDown={handleKeyNav}
              disabled={disabled}
              data-testid={`${type}.switcher`}
              aria-haspopup='menu'
              aria-expanded={isVisible}
              aria-label={label || 'menu'}
              $marginRight='0rem'
            >
              {Icon_Dots}
            </CircleButton>
            {label && !round ? <MobileLabel>{label}</MobileLabel> : null}
          </MobileWrapper>
        ) : (
          <PillButton
            type='button'
            name='Toggle'
            $scale={scale}
            $variant='secondary'
            $shadow
            onClick={handleToggleClick}
            onKeyDown={handleKeyNav}
            disabled={disabled}
            data-testid={`${type}.switcher`}
            aria-haspopup='menu'
            aria-expanded={isVisible}
            $marginRight='0rem'
          >
            {currentOption}
          </PillButton>
        )}

        {isVisible && (
          <DropDown.Menu ref={menuRef} name='Menu'>
            {items}
          </DropDown.Menu>
        )}
      </DropDown>
    </>
  );
};

export default RSwitcher;
